import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  console.log("[ADMIN-LOGIN] Componente AdminLogin renderizado");

  // Verificar si ya hay una sesión activa o marca de admin
  React.useEffect(() => {
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    if (isAdmin) {
      console.log("[ADMIN-LOGIN] Usuario ya autenticado como admin (localStorage)");
      navigate('/admin/dashboard');
    }
    
    // Verificar si hay una sesión activa
    const checkSession = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (data.session) {
        console.log("[ADMIN-LOGIN] Sesión activa detectada:", data.session.user.email);
        
        // Si el correo es el del administrador, redirigir directamente
        if (data.session.user.email === 'n.guerravillalobos@gmail.com') {
          console.log("[ADMIN-LOGIN] Correo coincide con administrador conocido");
          localStorage.setItem('isAdmin', 'true');
          navigate('/admin/dashboard');
        }
      }
    };
    
    checkSession();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    console.log("[ADMIN-LOGIN] Intento de inicio de sesión con:", email);

    try {
      // 1. Intentar iniciar sesión con Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error("[ADMIN-LOGIN] Error al iniciar sesión:", error.message);
        setError("Credenciales inválidas. Por favor, intenta de nuevo.");
        setLoading(false);
        return;
      }

      console.log("[ADMIN-LOGIN] Inicio de sesión exitoso");
      
      // 2. VERIFICACIÓN DIRECTA: Si el correo coincide con el administrador conocido
      if (email === 'n.guerravillalobos@gmail.com') {
        console.log("[ADMIN-LOGIN] Usuario verificado como administrador");
        localStorage.setItem('isAdmin', 'true');
        navigate('/admin/dashboard');
        return;
      }
      
      // 3. VERIFICACIÓN POR BASE DE DATOS: Verificar si el usuario es administrador
      console.log("[ADMIN-LOGIN] Consultando tabla de administradores...");
      const { data: adminData, error: adminError } = await supabase
        .from('admins')
        .select('*')
        .eq('user_id', data.user?.id);

      console.log("[ADMIN-LOGIN] Resultado de consulta:", adminData);
      
      if (adminError) {
        console.error("[ADMIN-LOGIN] Error al verificar estado de admin:", adminError.message);
        setError("Error al verificar permisos de administrador.");
        // Cerrar sesión ya que no se pudo verificar
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }

      if (adminData && adminData.length > 0) {
        console.log("[ADMIN-LOGIN] Usuario encontrado en tabla de admins:", adminData);
        localStorage.setItem('isAdmin', 'true');
        navigate('/admin/dashboard');
      } else {
        console.log("[ADMIN-LOGIN] Usuario no encontrado en tabla de admins");
        setError("No tienes permisos de administrador.");
        // Cerrar sesión ya que no es administrador
        await supabase.auth.signOut();
      }
    } catch (error) {
      console.error("[ADMIN-LOGIN] Error inesperado:", error);
      setError("Error en el servidor. Por favor, intenta de nuevo más tarde.");
    } finally {
      setLoading(false);
    }
  };

  // Función para establecer manualmente el estado de administrador (para depuración)
  const bypassLogin = () => {
    console.log("[ADMIN-LOGIN] Acceso directo activado");
    localStorage.setItem('isAdmin', 'true');
    navigate('/admin/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-purple-900 flex justify-center items-center p-4">
      <div className="w-full max-w-md bg-gray-800 rounded-lg shadow-xl overflow-hidden">
        <div className="p-6 sm:p-8">
          <h2 className="text-3xl font-bold text-white text-center mb-6">
            Panel de Administración
          </h2>
          
          {error && (
            <div className="bg-red-900/60 border border-red-500 text-white px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Correo electrónico
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-purple-500 focus:ring-purple-500 focus:outline-none"
                placeholder="correo@ejemplo.com"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                Contraseña
              </label>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-purple-500 focus:ring-purple-500 focus:outline-none"
                placeholder="••••••••"
              />
            </div>
            
            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium py-3 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 transition-all disabled:opacity-70"
              >
                {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
              </button>
            </div>
          </form>
          
          {/* Botón de acceso directo para depuración */}
          <div className="mt-4 text-center">
            <button 
              onClick={bypassLogin}
              className="text-xs text-gray-500 hover:text-purple-400 transition-colors"
              title="Solo para pruebas"
            >
              Acceso directo (desarrollo)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin; 